/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Text } from '@/primitives/Text';

import { cleanActorName, cleanResourcePath } from './utils/aclGrouping';
import {
  actorGroupLabelStyle,
  actorGroupNameStyle,
  actorNameStyle,
  actorTypeBadgeStyle,
  allowActionStyle,
  capabilityTextStyle,
  denyActionStyle,
  resourcePathStyle,
  ruleActionsStyle,
  ruleHeaderStyle,
  ruleIdStyle,
  ruleItemStyle,
  rulesContainerStyle,
  rulesCountBadgeStyle,
} from './utils/styles';
import { ACLRule } from './utils/types';

interface ActorGroupProps {
  actor: string;
  actorType: string;
  rules: ACLRule[];
}

export const createActorGroupItem = (
  { actor, actorType, rules }: ActorGroupProps,
  t: ReturnType<typeof useMimicTranslation<'configPrettyViewer'>>['t'],
) => {
  const label = (
    <div style={actorGroupLabelStyle}>
      <div style={actorGroupNameStyle}>
        <Text style={actorNameStyle}>{actor === 'ANY' ? t('aclProtection.anyActor') : cleanActorName(actor)}</Text>
        <span style={actorTypeBadgeStyle}>{actorType}</span>
      </div>
      <span style={rulesCountBadgeStyle}>{t('aclProtection.rulesCount', { count: rules.length })}</span>
    </div>
  );

  const children = (
    <div style={rulesContainerStyle}>
      {rules.map((rule) => (
        <div key={rule.ruleId ? `rule-${rule.ruleId}` : `${rule.action}-${rule.resource}`} style={ruleItemStyle}>
          <div style={ruleHeaderStyle}>
            <div style={ruleActionsStyle}>
              <span style={rule.action.includes('allow') ? allowActionStyle : denyActionStyle}>
                {rule.action.replace('_with_audit', '').replace('_', ' ')}
              </span>
              <span style={capabilityTextStyle}>{rule.capability}</span>
            </div>
            {rule.ruleId && <span style={ruleIdStyle}>#{rule.ruleId}</span>}
          </div>
          <div style={resourcePathStyle}>{cleanResourcePath(rule.resource)}</div>
        </div>
      ))}
    </div>
  );

  return { key: actor, label, children };
};
